import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import BookCover from '../components/BookCover';
import FlipbookViewer from '../components/FlipbookViewer';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster, toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { BookOpen, ArrowLeft, Loader2, Star, MessageSquare, X, Heart, ShoppingCart, Share2, Copy, Link2, Eye } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { loginWithGoogle } from '../utils/auth';
import { updateCartBadge } from '../utils/cart';

import { BACKEND_URL, API } from '../utils/api';
import { authFetch, clearToken } from '../utils/session';
const R2_URL = process.env.REACT_APP_R2_URL || BACKEND_URL + "/api/static";
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

class PayPalErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: false }; }
  componentDidCatch() { }
  render() { return this.props.children; }
}

const BookDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [showFlipbook, setShowFlipbook] = useState(false);
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allBooks, setAllBooks] = useState([]);
  const [purchased, setPurchased] = useState(false);
  // Review state
  const [showReview, setShowReview] = useState(false);
  const [reviewName, setReviewName] = useState('');
  const [reviewEmail, setReviewEmail] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  // User state
  const [user, setUser] = useState(null);
  const [inWishlist, setInWishlist] = useState(false);
  const [inCart, setInCart] = useState(false);
  const [userLibrary, setUserLibrary] = useState([]);
  const [showShare, setShowShare] = useState(false);

  // Memoize filtered book lists to avoid recomputing on every render
  const otherBooks = useMemo(
    () => allBooks.filter(b => b.slug !== slug && !userLibrary.includes(b.slug)),
    [allBooks, slug, userLibrary]
  );

  // Fetch book from API
  useEffect(() => {
    // Reset states for new book
    setPurchased(false);
    setInWishlist(false);
    setInCart(false);
    setShowFlipbook(false);
    setLoading(true);

    fetch(`${API}/books/${slug}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((apiBook) => {
        setBook({
          ...apiBook,
          id: apiBook.book_id || apiBook.slug,
          coverUrl: `${R2_URL}/books/${apiBook.imageFolder}/cover.webp`,
          pdfCoverUrl: `${R2_URL}/books/${apiBook.imageFolder}/page_1.webp`,
          imagePages: apiBook.pageCount,
          pages: apiBook.pages || [],
          coverColors: apiBook.coverColors || ['#6366F1', '#4F46E5'],
        });
        setLoading(false);
      })
      .catch(() => {
        setBook(null);
        setLoading(false);
      });

    // Fetch all books for search
    fetch(`${API}/books`).then(r => r.json()).then(books => {
      if (books?.length) setAllBooks(books.map(b => ({ ...b, id: b.book_id || b.slug, coverUrl: `${R2_URL}/books/${b.imageFolder}/cover.webp` })));
    }).catch(() => {});

    // Fetch user state
    authFetch(`${API}/auth/me`, {  }).then(r => r.ok ? r.json() : null).then(u => {
      if (u) {
        setUser(u);
        setReviewName(u.name || '');
        setReviewEmail(u.email || '');
        authFetch(`${API}/user/library`, {  }).then(r => r.ok ? r.json() : []).then(lib => { setUserLibrary(lib || []); if (lib?.includes(slug)) setPurchased(true); });
        authFetch(`${API}/user/wishlist`, {  }).then(r => r.ok ? r.json() : []).then(wl => { if (wl?.includes(slug)) setInWishlist(true); });
        authFetch(`${API}/user/cart`, {  }).then(r => r.ok ? r.json() : []).then(c => { if (c?.includes(slug)) setInCart(true); });
      }
    }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && !showFlipbook && !showReview) navigate('/');
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [navigate, showFlipbook, showReview]);

  const { executeRecaptcha } = useGoogleReCaptcha();

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-dvh bg-background font-sans flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Libro no encontrado</p>
          <button onClick={() => navigate('/')} className="text-sm font-medium hover:underline">Volver</button>
        </div>
      </div>
    );
  }

  const submitReview = async () => {
    if (!reviewName.trim() || !reviewEmail.trim() || !reviewText.trim()) { toast.error('Completa todos los campos'); return; }
    setReviewSubmitting(true);
    try {
      const captchaToken = executeRecaptcha ? await executeRecaptcha('submit_review') : '';
      const res = await authFetch(`${API}/books/${slug}/reviews`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: reviewName, email: reviewEmail, rating: reviewRating, text: reviewText, captcha_token: captchaToken }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Error'); }
      toast.success('¡Gracias por tu reseña!');
      setShowReview(false); setReviewName(''); setReviewEmail(''); setReviewRating(5); setReviewText('');
    } catch (e) { toast.error(e.message || 'Error al enviar'); }
    finally { setReviewSubmitting(false); }
  };

  const shareUrl = `${window.location.origin}/libro/${slug}`;
  const ogImage = book?.coverUrl || '';

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Enlace copiado');
    setShowShare(false);
  };

  return (
    <div className="min-h-dvh bg-background font-sans">
      {book && book.title && (() => {
        const catText = book.category ? ` del ${book.category}` : '';
        const charsText = book.characters?.length ? ` con ${book.characters.slice(0, 3).join(', ')}` : '';
        const autoDesc = `${book.description || `Librito cristiano ilustrado${catText}: ${book.title}${charsText}.`} Para niños de 3 a 10 años. Formato digital interactivo. Ideal para familia, escuela dominical e iglesias.`;
        return (
        <Helmet>
          <title>{`${book.title} — Librito Cristiano Ilustrado para Niños | Museo de la Fe`}</title>
          <meta name="description" content={autoDesc.slice(0, 160)} />
          <meta property="og:title" content={`${book.title} — Museo de la Fe`} />
          <meta property="og:description" content={autoDesc.slice(0, 120)} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:url" content={`https://museodelafe.com/libro/${slug}`} />
          <meta property="og:type" content="product" />
          <meta property="og:locale" content="es_ES" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${book.title} — Museo de la Fe`} />
          <meta name="twitter:description" content={autoDesc.slice(0, 120)} />
          <meta name="twitter:image" content={ogImage} />
          <link rel="canonical" href={`https://museodelafe.com/libro/${slug}`} />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": book.title,
            "description": autoDesc,
            "image": ogImage,
            "brand": { "@type": "Brand", "name": "Museo de la Fe" },
            "category": book.category || "Librito Cristiano",
            "audience": { "@type": "PeopleAudience", "suggestedMinAge": 3, "suggestedMaxAge": 10 },
            "inLanguage": "es",
            "offers": { "@type": "Offer", "price": book.price?.toFixed(2), "priceCurrency": "USD", "availability": "https://schema.org/InStock", "url": `https://museodelafe.com/libro/${slug}`, "seller": { "@type": "Organization", "name": "Museo de la Fe" } }
          })}</script>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://museodelafe.com" },
              { "@type": "ListItem", "position": 2, "name": book.category || "Libritos", "item": "https://museodelafe.com" },
              { "@type": "ListItem", "position": 3, "name": book.title, "item": `https://museodelafe.com/libro/${slug}` }
            ]
          })}</script>
        </Helmet>
        );
      })()}
      <Toaster position="top-center" />
      <Header onSearchClick={() => navigate('/')} searchText={`Buscar ${allBooks.length} libros`} />

      <div className={`max-w-3xl mx-auto px-6 pt-4 ${!purchased ? 'pb-28 md:pb-16' : 'pb-16'}`}>
        {/* Back */}
        <motion.button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <ArrowLeft className="size-4" /> Volver
        </motion.button>

        {/* Horizontal layout: cover left, info right */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-center md:items-start">
          {/* Left: Cover + icon actions */}
          <motion.div
            className="shrink-0 flex flex-col items-center gap-4"
            initial={{ opacity: 0, y: 30, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="w-44 h-60 sm:w-52 sm:h-[280px] rounded-xl overflow-hidden shadow-lg bg-secondary">
              {book.pdfCoverUrl ? (
                <img src={book.pdfCoverUrl} alt={`Librito cristiano ilustrado ${book.title} para niños de 3 a 10 años — Museo de la Fe`} className="w-full h-full object-contain" />
              ) : (
                <BookCover book={book} hideTitle />
              )}
            </div>
            {/* Icon action bar */}
            <div className="flex items-center gap-1.5">
              {(book.imagePages || (book.pages && book.pages.length > 0)) && (
                <button
                  data-testid="book-preview-btn"
                  onClick={() => setShowFlipbook(true)}
                  title="Vista previa"
                  className="flex items-center justify-center size-9 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 hover:bg-secondary/50 transition-colors"
                >
                  <Eye className="size-4" />
                </button>
              )}
              <button
                data-testid="book-review-btn"
                onClick={() => { if (!user) { loginWithGoogle(`/libro/${slug}`); return; } setShowReview(true); }}
                title="Dejar reseña"
                className="flex items-center justify-center size-9 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 hover:bg-secondary/50 transition-colors"
              >
                <MessageSquare className="size-4" />
              </button>
              <button
                data-testid="book-wishlist-btn"
                onClick={async () => {
                  if (!user) { loginWithGoogle(`/libro/${slug}`); return; }
                  if (inWishlist) { await authFetch(`${API}/user/wishlist/${slug}`, { method: 'DELETE' }); setInWishlist(false); }
                  else { await authFetch(`${API}/user/wishlist/${slug}`, { method: 'POST' }); setInWishlist(true); toast.success('Agregado a favoritos'); }
                }}
                title={inWishlist ? 'En favoritos' : 'Agregar a favoritos'}
                className={`flex items-center justify-center size-9 rounded-full border transition-colors ${inWishlist ? 'border-red-200 text-red-500 bg-red-50 hover:bg-red-100' : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 hover:bg-secondary/50'}`}
              >
                <Heart className={`size-4 ${inWishlist ? 'fill-red-500' : ''}`} />
              </button>
              <button
                data-testid="book-share-btn"
                onClick={() => setShowShare(true)}
                title="Compartir"
                className="flex items-center justify-center size-9 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 hover:bg-secondary/50 transition-colors"
              >
                <Share2 className="size-4" />
              </button>
            </div>
          </motion.div>

          {/* Right: Info */}
          <div className="flex-1 min-w-0 text-center md:text-left">
            {book.category && (
              <motion.div
                className="mb-3"
                initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="inline-flex px-3 py-1 rounded-full bg-secondary text-xs font-medium text-muted-foreground">
                  {book.category}
                </span>
              </motion.div>
            )}

            <motion.h1
              className="text-xl sm:text-2xl font-bold mb-2"
              initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              {book.title}
            </motion.h1>

            {/* Description */}
            <motion.p
              className="text-muted-foreground text-sm leading-relaxed mb-5"
              initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              {book.description}
            </motion.p>

            {/* Price + CTA block — high visibility */}
            <motion.div
              className="mb-6 p-5 rounded-2xl bg-secondary/40 border border-border/50"
              initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.5, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              {purchased ? (
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <button
                    data-testid="book-read-btn"
                    onClick={() => setShowFlipbook(true)}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 h-12 px-8 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors"
                  >
                    <BookOpen className="size-4" /> Leer Libro
                  </button>
                  <span className="text-xs text-emerald-600 font-medium">En tu biblioteca</span>
                </div>
              ) : (
                <>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-2xl font-bold text-foreground">${book.price?.toFixed(2)}</span>
                    <span className="text-sm text-muted-foreground">USD</span>
                  </div>
                  <button
                    data-testid="book-add-cart-btn"
                    onClick={async () => {
                      if (!user) { loginWithGoogle(`/libro/${slug}`); return; }
                      if (inCart) { navigate('/carrito'); return; }
                      await authFetch(`${API}/user/cart/${slug}`, { method: 'POST' });
                      setInCart(true);
                      updateCartBadge();
                      toast.success('Agregado al carrito');
                    }}
                    className={`w-full flex items-center justify-center gap-2 h-12 rounded-xl text-sm font-semibold transition-all duration-200 ${inCart ? 'bg-secondary text-foreground border border-border hover:bg-secondary/80' : 'bg-foreground text-background hover:bg-foreground/90 hover:scale-[1.01] active:scale-[0.99]'}`}
                  >
                    <ShoppingCart className="size-4" /> {inCart ? 'Ver carrito' : 'Agregar al carrito'}
                  </button>
                </>
              )}
            </motion.div>

            {/* Details table */}
            <motion.div
              className="divide-y divide-border"
              initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex justify-between py-3">
                <span className="text-sm text-muted-foreground">Páginas</span>
                <span className="text-sm font-medium">{book.pageCount || book.imagePages || '-'}</span>
              </div>
              {book.category && (
                <div className="flex justify-between py-3">
                  <span className="text-sm text-muted-foreground">Categoría</span>
                  <span className="text-sm font-medium">{book.category}</span>
                </div>
              )}
              {book.characters?.length > 0 && (
                <div className="flex justify-between py-3">
                  <span className="text-sm text-muted-foreground">Personajes</span>
                  <span className="text-sm font-medium text-right">{book.characters.join(', ')}</span>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA bar */}
      {!purchased && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-background/95 backdrop-blur-xl border-t border-border px-4 py-3 safe-area-pb">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground truncate">{book.title}</p>
              <p className="text-lg font-bold">${book.price?.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">USD</span></p>
            </div>
            <button
              data-testid="mobile-add-cart-btn"
              onClick={async () => {
                if (!user) { loginWithGoogle(`/libro/${slug}`); return; }
                if (inCart) { navigate('/carrito'); return; }
                await authFetch(`${API}/user/cart/${slug}`, { method: 'POST' });
                setInCart(true);
                updateCartBadge();
                toast.success('Agregado al carrito');
              }}
              className={`shrink-0 flex items-center gap-2 h-11 px-6 rounded-xl text-sm font-semibold transition-colors ${inCart ? 'bg-secondary text-foreground border border-border' : 'bg-foreground text-background hover:bg-foreground/90'}`}
            >
              <ShoppingCart className="size-4" /> {inCart ? 'Ver carrito' : 'Agregar'}
            </button>
          </div>
        </div>
      )}

      {/* Bundle promo — only if not purchased and there are other books */}
      {!purchased && (() => {
        if (otherBooks.length < 2) return null;
        const bundleBooks = otherBooks.slice(0, 2);
        const originalPrice = bundleBooks.reduce((s, b) => s + (b.price || 0), 0);
        const bundlePrice = Math.round(originalPrice * 0.75 * 100) / 100;
        const savings = Math.round((originalPrice - bundlePrice) * 100) / 100;
        return (
          <div className="max-w-3xl mx-auto px-6 mb-12">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60 rounded-2xl p-6">
              <div className="flex flex-col sm:flex-row items-center gap-5">
                <div className="flex items-center gap-2 shrink-0">
                  {bundleBooks.map(b => (
                    <div key={b.slug} className="w-16 h-20 rounded-lg overflow-hidden shadow-sm">
                      <BookCover book={b} hideTitle />
                    </div>
                  ))}
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Oferta especial</p>
                  <h3 className="text-base font-bold text-foreground mb-1">Lleva 4 o más y obtén 5% de descuento</h3>
                  <p className="text-xs text-muted-foreground mb-3">Agrega más historias al carrito. A partir del 4to librito, cada uno tiene 5% de descuento.</p>
                  <div className="flex items-center gap-3 justify-center sm:justify-start">
                    <button
                      onClick={async () => {
                        if (!user) { loginWithGoogle(`/libro/${slug}`); return; }
                        for (const b of bundleBooks) { await authFetch(`${API}/user/cart/${b.slug}`, { method: 'POST' }); }
                        updateCartBadge();
                        toast.success('¡Libritos agregados al carrito!');
                        navigate('/carrito');
                      }}
                      className="h-9 px-4 rounded-xl bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 transition-colors"
                    >
                      Agregar al carrito
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* More books */}
      {otherBooks.length > 0 && (
        <div className="max-w-3xl mx-auto px-6 pb-16">
          <h2 className="text-lg font-bold mb-4">Más historias para tus peques</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {otherBooks.slice(0, 5).map(b => (
              <button key={b.slug} onClick={() => navigate(`/libro/${b.slug}`)} className="text-left group">
                <div className="aspect-[3/4] rounded-lg overflow-hidden mb-1.5 group-hover:scale-105 transition-transform duration-200 shadow-sm">
                  <BookCover book={b} hideTitle />
                </div>
                <p className="text-[10px] font-medium truncate">{b.title}</p>
                <p className="text-[10px] text-emerald-600 font-medium">${b.price?.toFixed(2)}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Flipbook */}
      {showFlipbook && <FlipbookViewer
        book={book}
        onClose={() => setShowFlipbook(false)}
        preview={!purchased}
        onBuy={async () => {
          setShowFlipbook(false);
          if (!user) { loginWithGoogle(`/libro/${slug}`); return; }
          if (!inCart) { await authFetch(`${API}/user/cart/${slug}`, { method: 'POST' }); setInCart(true); }
          navigate('/carrito');
        }}
      />}

      {/* Review modal */}
      <Dialog open={showReview} onOpenChange={setShowReview}>
        <DialogContent className="sm:max-w-sm !z-[200]">
          <h2 className="text-lg font-bold mb-1">Dejar una reseña</h2>
          <p className="text-xs text-muted-foreground mb-5">{book.title}</p>
          <div className="space-y-4">
            {user ? (
              <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-xl">
                {user.picture && <img src={user.picture} alt="" className="size-8 rounded-full" />}
                <div>
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-[10px] text-muted-foreground">{user.email}</p>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Tu nombre</label>
                  <input value={reviewName} onChange={(e) => setReviewName(e.target.value)} maxLength={50} className="w-full h-10 px-3 rounded-xl bg-secondary border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20" placeholder="Nombre" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Tu correo</label>
                  <input type="email" value={reviewEmail} onChange={(e) => setReviewEmail(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-secondary border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20" placeholder="correo@ejemplo.com" />
                </div>
              </>
            )}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Calificación</label>
              <div className="flex gap-1">
                {[1,2,3,4,5].map(s => (
                  <button key={s} type="button" onClick={() => setReviewRating(s)} className="p-0.5">
                    <Star className={`size-6 transition-colors ${s <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-border'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Comentario <span className="text-muted-foreground/40">({reviewText.length}/140)</span></label>
              <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value.slice(0, 140))} maxLength={140} className="w-full h-20 px-3 py-2 rounded-xl bg-secondary border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20 resize-none" placeholder="¿Qué te pareció este libro?" />
            </div>
            <button onClick={submitReview} disabled={reviewSubmitting} className="w-full h-10 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors disabled:opacity-50">
              {reviewSubmitting ? 'Enviando...' : 'Enviar reseña'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share modal */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent className="sm:max-w-xs !z-[200]">
          <h2 className="text-lg font-bold mb-1">Compartir</h2>
          <p className="text-xs text-muted-foreground mb-5">{book.title}</p>
          <div className="grid grid-cols-2 gap-2">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${book.title} — Museo de la Fe\n${shareUrl}`)}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 h-10 rounded-xl bg-[#25D366] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <svg className="size-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.11.546 4.093 1.504 5.818L0 24l6.335-1.652A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-1.97 0-3.837-.53-5.445-1.454l-.39-.232-3.75.978.998-3.648-.254-.404A9.72 9.72 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z"/></svg>
              WhatsApp
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 h-10 rounded-xl bg-[#1877F2] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <svg className="size-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Facebook
            </a>
            <button
              onClick={() => { window.open(`https://www.instagram.com/`, '_blank'); copyLink(); }}
              className="flex items-center justify-center gap-2 h-10 rounded-xl bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <svg className="size-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              Instagram
            </button>
            <button
              onClick={copyLink}
              className="flex items-center justify-center gap-2 h-10 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
            >
              <Link2 className="size-4" />
              Copiar enlace
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default BookDetailPage;
